'use client';

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// Types
import { RequestConfig, UserProfile } from '@/types';
import { Project } from '@shared/types';
import { UsersSlice } from './user/types';
import { ChatSlice, ChatStage } from './chat/types';
import { ProjectsSlice } from './project/types';
import { ProjectMemberViewMode, UserConfigSlice } from './userConfig/types';
import { OptionDataSlice } from './optionData/types';
import { Message } from '@copilotkit/runtime-client-gql';

// Constants
import { MasterCategoryType } from '@shared/constants';

// Slices
import { AuthSlice, createAuthSlice } from './auth/authSlice';
import { createUsersSlice } from './user/userSlice';
import { createChatSlice } from './chat/chatSlice';
import { createProjectsSlice } from './project/projectSlice';
import { createUserConfigSlice } from './userConfig/userConfigSlice';
import { createOptionDataSlice } from './optionData/optionDataSlice';

// Combined store type
export type AppStore = AuthSlice &
  UsersSlice &
  ChatSlice &
  ProjectsSlice &
  UserConfigSlice &
  OptionDataSlice;

// Create the main store combining all slices
export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createUsersSlice(...a),
        ...createChatSlice(...a),
        ...createProjectsSlice(...a),
        ...createUserConfigSlice(...a),
        ...createOptionDataSlice(...a),
      }),
      {
        name: 'app-storage',
        partialize: (state) => ({
          // Auth state
          isAuthorized: state.isAuthorized,

          // Users state
          users: state.users,
          hasUserProfile: state.hasUserProfile,

          // Projects state
          projects: state.projects,

          // Chat State
          chatMessages: state.chatMessages,
          chatStage: state.chatStage,
          currentContextId: state.currentContextId,
          richTextEditorValues: state.richTextEditorValues,

          // User Config State
          userConfig: state.userConfig,

          // Option Data state
          optionData: state.optionData,
          optionDataLoadingStates: state.optionDataLoadingStates,
          optionDataLastFetched: state.optionDataLastFetched,
        }),
      },
    ),
    { enabled: process.env.NODE_ENV === 'development' },
  ),
);

// Auth selectors
export const useIsAuthorized = () =>
  useAppStore(useShallow((state) => state.isAuthorized));
export const useIsShouldForceLogout = () =>
  useAppStore(useShallow((state) => state.shouldForceLogout));
export const useMessage = () =>
  useAppStore(useShallow((state) => state.message));
export const useIsLoggingOut = () =>
  useAppStore(useShallow((state) => state.isLoggingOut));
export const useHasBeenAuthorized = () =>
  useAppStore(useShallow((state) => state.hasBeenAuthorized));

// Auth actions
export const useSetAuthorized = () =>
  useAppStore(useShallow((state) => state.setAuthorized));
export const useSetMessage = () =>
  useAppStore(useShallow((state) => state.setMessage));
export const useSetForceLogoutWithMessage = () =>
  useAppStore(useShallow((state) => state.setForceLogoutWithMessage));
export const useSetHasBeenAuthorized = () =>
  useAppStore(useShallow((state) => state.setHasBeenAuthorized));
export const useResetAuth = () =>
  useAppStore(useShallow((state) => state.resetAuth));
export const useSetIsLoggingOut = () =>
  useAppStore(useShallow((state) => state.setIsLoggingOut));

// Users selectors
export const useUsers = () => useAppStore(useShallow((state) => state.users));
export const useIsLoading = () =>
  useAppStore(useShallow((state) => state.isLoading));
export const useHasUserProfile = () =>
  useAppStore(useShallow((state) => state.hasUserProfile));
export const useIsAllUsersFetched = () =>
  useAppStore(useShallow((state) => state.isAllUsersFetched));

// Users actions
export const useSetUsers = () =>
  useAppStore(useShallow((state) => state.setUsers));
export const useSetIsLoading = () =>
  useAppStore(useShallow((state) => state.setLoading));
export const useSetHasUserProfile = () =>
  useAppStore(useShallow((state) => state.setHasUserProfile));
export const useSetIsAllUsersFetched = () =>
  useAppStore(useShallow((state) => state.setAllUsersFetched));
export const useFetchUsers = () =>
  useAppStore(useShallow((state) => state.fetchUsers));
export const useResetUsers = () =>
  useAppStore(useShallow((state) => state.resetUsers));

// WebSocket User actions
export const useUpdateUser = () =>
  useAppStore(useShallow((state) => state.updateUser));
export const useRemoveUser = () =>
  useAppStore(useShallow((state) => state.removeUser));
export const useAddUser = () =>
  useAppStore(useShallow((state) => state.addUser));

// Projects selectors
export const useProjects = () =>
  useAppStore(useShallow((state) => state.projects));
export const useIsProjectsLoading = () =>
  useAppStore(useShallow((state) => state.isProjectsLoading));
export const useIsProjectsFetched = () =>
  useAppStore(useShallow((state) => state.isProjectsFetched));

// Projects actions
export const useSetProjects = () =>
  useAppStore(useShallow((state) => state.setProjects));
export const useSetProjectsLoading = () =>
  useAppStore(useShallow((state) => state.setProjectsLoading));
export const useSetProjectsFetched = () =>
  useAppStore(useShallow((state) => state.setProjectsFetched));
export const useFetchProjects = () =>
  useAppStore(useShallow((state) => state.fetchProjects));
export const useResetProjects = () =>
  useAppStore(useShallow((state) => state.resetProjects));

// WebSocket Project actions
export const useSetArchiveProject = () =>
  useAppStore(useShallow((state) => state.setArchiveProject));
export const useRemoveProject = () =>
  useAppStore(useShallow((state) => state.removeProject));
export const useAddProject = () =>
  useAppStore(useShallow((state) => state.addProject));

export const useIsOptionDataLoading = () =>
  useAppStore(useShallow((state) => state.isOptionDataLoading));
export const useIsOptionDataFetched = () =>
  useAppStore(useShallow((state) => state.isOptionDataFetched));

// New advanced selectors following sample pattern
export const useGetOptionDataByType = () =>
  useAppStore(useShallow((state) => state.getOptionDataByType));
export const useShouldRefetchOptionData = () =>
  useAppStore(useShallow((state) => state.shouldRefetchOptionData));

// Option Data actions
export const useSetOptionData = () =>
  useAppStore(useShallow((state) => state.setOptionData));
export const useSetOptionDataLoading = () =>
  useAppStore(useShallow((state) => state.setOptionDataLoading));
export const useSetOptionDataFetched = () =>
  useAppStore(useShallow((state) => state.setOptionDataFetched));
export const useFetchOptionDataByTypes = () =>
  useAppStore(useShallow((state) => state.fetchOptionDataByTypes));
export const useClearOptionDataByType = () =>
  useAppStore(useShallow((state) => state.clearOptionDataByType));
export const useResetOptionData = () =>
  useAppStore(useShallow((state) => state.resetOptionData));

// Direct store access for non-component code
export const appStoreActions = {
  // Auth actions
  setAuthorized: (value: boolean) =>
    useAppStore.getState().setAuthorized(value),
  setMessage: (value: string) => useAppStore.getState().setMessage(value),
  setForceLogoutWithMessage: (message: string) =>
    useAppStore.getState().setForceLogoutWithMessage(message),
  setIsLoggingOut: (value: boolean) =>
    useAppStore.getState().setIsLoggingOut(value),
  setHasBeenAuthorized: (value: boolean) =>
    useAppStore.getState().setHasBeenAuthorized(value),
  resetAuth: () => useAppStore.getState().resetAuth(),

  // Users actions
  setUsers: (users: UserProfile[]) => useAppStore.getState().setUsers(users),
  setIsLoading: (loading: boolean) =>
    useAppStore.getState().setLoading(loading),
  setIsFetched: (fetched: boolean) =>
    useAppStore.getState().setFetched(fetched),
  setAllUsersFetched: (allUsersFetched: boolean) =>
    useAppStore.getState().setAllUsersFetched(allUsersFetched),
  fetchUsers: ({
    requestConfig,
    force,
  }: {
    requestConfig?: RequestConfig;
    force?: boolean;
  }) => useAppStore.getState().fetchUsers({ requestConfig, force }),
  resetUsers: () => useAppStore.getState().resetUsers(),

  // WebSocket User actions
  updateUser: (user: UserProfile) => useAppStore.getState().updateUser(user),
  removeUser: (userId: string) => useAppStore.getState().removeUser(userId),
  addUser: (user: UserProfile) => useAppStore.getState().addUser(user),

  // Chat actions
  setChatMessages: (chatMessages: Message[]) =>
    useAppStore.getState().setChatMessages(chatMessages),
  setChatLoading: (isChatLoading: boolean) =>
    useAppStore.getState().setChatLoading(isChatLoading),
  setChatStage: (chatStage: ChatStage) =>
    useAppStore.getState().setChatStage(chatStage),
  setChatInitialized: (isChatInitialized: boolean) =>
    useAppStore.getState().setChatInitialized(isChatInitialized),
  setChatResetFlag: (shouldResetChat: boolean) =>
    useAppStore.getState().setChatResetFlag(shouldResetChat),
  setCurrentContextId: (currentContextId: string) =>
    useAppStore.getState().setCurrentContextId(currentContextId),
  resetChat: () => useAppStore.getState().resetChat(),
  setIsWaitingForUserResponse: (isWaiting: boolean) =>
    useAppStore.getState().setIsWaitingForUserResponse(isWaiting),
  setIsConfirmationAction: (isActive: boolean) =>
    useAppStore.getState().setIsConfirmationAction(isActive),

  // Projects actions
  setProjects: (projects: Project[]) =>
    useAppStore.getState().setProjects(projects),
  setProjectsLoading: (loading: boolean) =>
    useAppStore.getState().setProjectsLoading(loading),
  setProjectsFetched: (fetched: boolean) =>
    useAppStore.getState().setProjectsFetched(fetched),
  fetchProjects: ({
    force,
    requestConfig,
  }: {
    force?: boolean;
    requestConfig?: RequestConfig;
  }) => useAppStore.getState().fetchProjects({ force, requestConfig }),
  resetProjects: () => useAppStore.getState().resetProjects(),

  // WebSocket Project actions
  setArchiveProject: (code: string, archive: boolean) =>
    useAppStore.getState().setArchiveProject(code, archive),
  removeProject: (projectIds: string[]) =>
    useAppStore.getState().removeProject(projectIds),
  addProject: (project: Project) => useAppStore.getState().addProject(project),

  // Option Data actions
  setOptionDataLoading: (loading: boolean) =>
    useAppStore.getState().setOptionDataLoading(loading),
  setOptionDataFetched: (fetched: boolean) =>
    useAppStore.getState().setOptionDataFetched(fetched),
  fetchOptionDataByTypes: (
    types: MasterCategoryType[],
    force?: boolean,
    token?: string,
  ) => useAppStore.getState().fetchOptionDataByTypes(types, force, token),
  getOptionDataByType: (type: MasterCategoryType) =>
    useAppStore.getState().getOptionDataByType(type),
  clearOptionDataByType: (type?: MasterCategoryType) =>
    useAppStore.getState().clearOptionDataByType(type),
  resetOptionData: () => useAppStore.getState().resetOptionData(),

  // User config actions
  setProjectMemberViewMode: (viewMode: ProjectMemberViewMode) =>
    useAppStore.getState().setProjectMemberViewMode(viewMode),
  setShowRoleLabel: (showRoleLabel: boolean) =>
    useAppStore.getState().setShowRoleLabel(showRoleLabel),
  resetUserConfig: () => useAppStore.getState().resetUserConfig(),

  // Chat input actions
  resetChatInput: () => useAppStore.getState().resetChatInput(),

  // Rich Text Editor actions
  setRichTextEditorValue: (key: string, value: string) =>
    useAppStore.getState().setRichTextEditorValue(key, value),
  clearRichTextEditorValue: (key: string) =>
    useAppStore.getState().clearRichTextEditorValue(key),
  clearAllRichTextEditorValues: () =>
    useAppStore.getState().clearAllRichTextEditorValues(),

  // Global store actions
  clearAllStores: () => {
    const { resetAuth, resetUsers, resetProjects, resetChat, resetOptionData } =
      useAppStore.getState();

    // Reset all stores in parallel
    resetAuth();
    resetUsers();
    resetProjects();
    resetChat();
    resetOptionData();
  },
} as const;

// Chat selectors
export const useChatMessages = () =>
  useAppStore(useShallow((state) => state.chatMessages));
export const useIsChatLoading = () =>
  useAppStore(useShallow((state) => state.isChatLoading));
export const useChatStage = () =>
  useAppStore(useShallow((state) => state.chatStage));
export const useChatInitialized = () =>
  useAppStore(useShallow((state) => state.isChatInitialized));
export const useShouldResetChat = () =>
  useAppStore(useShallow((state) => state.shouldResetChat));
export const useCurrentContextId = () =>
  useAppStore(useShallow((state) => state.currentContextId));
export const useResetChat = () =>
  useAppStore(useShallow((state) => state.resetChat));
export const useIsWaitingForUserResponse = () =>
  useAppStore(useShallow((state) => state.isWaitingForUserResponse));
export const useSetIsWaitingForUserResponse = () =>
  useAppStore(useShallow((state) => state.setIsWaitingForUserResponse));
export const useIsConfirmationAction = () =>
  useAppStore(useShallow((state) => state.isConfirmationAction));
export const useSetIsConfirmationAction = () =>
  useAppStore(useShallow((state) => state.setIsConfirmationAction));
export const useSetChatLoading = () =>
  useAppStore(useShallow((state) => state.setChatLoading));

// Suggestion panel
export const useIsSuggestionOpen = () =>
  useAppStore(useShallow((state) => state.isSuggestionOpen));
export const useHasSuggestionAnimated = () =>
  useAppStore(useShallow((state) => state.hasSuggestionAnimated));
export const useSetIsSuggestionOpen = () =>
  useAppStore(useShallow((state) => state.setIsSuggestionOpen));
export const useSetHasSuggestionAnimated = () =>
  useAppStore(useShallow((state) => state.setHasSuggestionAnimated));
export const useResetSuggestionPanel = () =>
  useAppStore(useShallow((state) => state.resetSuggestionPanel));

// Rich Text Editor selectors
export const useRichTextEditorValue = (key: string) =>
  useAppStore(useShallow((state) => state.richTextEditorValues[key]));

// User Config selectors
export const useUserConfig = () =>
  useAppStore(useShallow((state) => state.userConfig));

// Utility functions
export const shouldHideToast = () => {
  const state = useAppStore.getState();
  return !state.isAuthorized || state.shouldForceLogout;
};

export const refreshUsersData = async (requestConfig?: RequestConfig) => {
  const { getUserProfile } = await import('@/actions/client/user');
  return await getUserProfile({ requestConfig });
};

export { ProjectMemberViewMode } from './userConfig/types';

// Types
export type { AuthSlice, AuthState, AuthActions } from './auth/types';
export type { UsersSlice, UsersState, UsersActions } from './user/types';
export type { ChatSlice, ChatState, ChatActions } from './chat/types';
export type {
  ProjectsSlice,
  ProjectsState,
  ProjectsActions,
} from './project/types';
export type {
  UserConfigSlice,
  UserConfigState,
  UserConfigActions,
} from './userConfig/types';
export type {
  OptionDataSlice,
  OptionDataState,
  OptionDataActions,
} from './optionData/types';
